'use client'

import React from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';

interface LocaleLinkProps {
  href: string;
  children: React.ReactNode;
  className?: string;
  locale?: string;
  [key: string]: any; // Allow other Link props
}

/**
 * LocaleLink component that automatically prefixes URLs with the locale
 *
 * Usage:
 *   <LocaleLink href="/privacy-policy">Privacy Policy</LocaleLink>
 *   // Renders as: /en/privacy-policy (or /de/privacy-policy based on current locale)
 *
 * You can also explicitly pass a locale:
 *   <LocaleLink href="/privacy-policy" locale="de">Datenschutz</LocaleLink>
 *
 * This prevents having to manually add locale prefixes everywhere
 */
const LocaleLink: React.FC<LocaleLinkProps> = ({ href, children, locale: explicitLocale, ...props }) => {
  const params = useParams();
  // Use explicit locale if provided, otherwise get from URL params, fallback to 'en'
  const locale = explicitLocale || (params?.locale as string) || 'en';

  // If href already starts with a locale prefix, don't add it again
  const hasLocalePrefix = href.startsWith('/en') || href.startsWith('/de');
  const localizedHref = hasLocalePrefix ? href : `/${locale}${href}`;

  return (
    <Link href={localizedHref} {...props}>
      {children}
    </Link>
  );
};

export default LocaleLink;
